import { useState, useEffect } from 'react'

/**
 * Custom hook that tracks the state of a media query using the Match Media API
 * @param query - The media query string to track (e.g., '(min-width: 768px)')
 * @returns boolean - Whether the media query currently matches
 */
export function useMediaQuery(query) {
  // Initialize state with current match status
  const [matches, setMatches] = useState(() => typeof window === 'undefined' ? false : window.matchMedia(query).matches)

  useEffect(() => {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
      return
    }

    const mediaQuery = window.matchMedia(query)

    // Handler function to update state when media query changes
    const handleChange = (event) => {
      setMatches(event.matches)
    }

    // Set initial state
    setMatches(mediaQuery.matches)

    // Add event listener for changes
    mediaQuery.addEventListener('change', handleChange)

    // Cleanup function to remove event listener
    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [query])

  return matches
}
// Usage example:
// import { useMediaQuery } from 'CustomHooks/useMediaQuery';
// const isMobile = useMediaQuery('(max-width: 767px)')
//     const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)')
//     const isDesktop = useMediaQuery('(min-width: 1024px)')
//     const isDarkMode = useMediaQuery('(prefers-color-scheme: dark)')
//     const isReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')